import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { House, ChartLine, Newspaper, ChartBar, ChatCircle, User, ShieldCheck, Bell, Users, Gear, SignOut, Sparkle, CurrencyDollar, Broadcast, Sun, Moon, Robot, ChartLineUp } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';

const navItems = [
  { to: '/dashboard', label: 'Dashboard', icon: House },
  { to: '/trading', label: 'Trading', icon: ChartLineUp },
  { to: '/smart-trade', label: 'Smart Trade AI', icon: Robot },
  { to: '/signals', label: 'Live Signals', icon: Broadcast },
  { to: '/charts', label: 'Live Charts', icon: ChartLine },
  { to: '/analysis', label: 'Analysis', icon: ChartBar },
  { to: '/news', label: 'News', icon: Newspaper },
  { to: '/chat', label: 'Live Chat', icon: ChatCircle, premium: true },
  { to: '/verification', label: 'Verification', icon: ShieldCheck },
  { to: '/pricing', label: 'Subscription', icon: CurrencyDollar },
  { to: '/referral', label: 'Referral', icon: Users },
  { to: '/profile', label: 'Profile', icon: User },
];

export default function AppLayout({ children }) {
  const loc = useLocation();
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const [notifs, setNotifs] = useState([]);
  const [showNotifs, setShowNotifs] = useState(false);

  useEffect(() => {
    if (!user) return;
    const load = async () => {
      try { const { data } = await api.get('/notifications'); setNotifs(data); } catch {}
    };
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [user]);

  const unread = notifs.filter(n => !n.read).length;

  return (
    <div className="flex min-h-screen bg-surface-base relative z-10">
      {/* Sidebar */}
      <aside className="hidden lg:flex w-64 flex-col glass-strong border-r border-white/5 fixed inset-y-0 left-0 z-30">
        <div className="p-6 border-b border-white/5">
          <Link to="/dashboard" className="flex items-center gap-2 group" data-testid="sidebar-logo">
            <div className="w-9 h-9 rounded-lg gradient-orange flex items-center justify-center shadow-glow-saffron">
              <Sparkle size={20} weight="fill" className="text-white" />
            </div>
            <div>
              <div className="font-heading font-bold text-lg leading-none">4xview</div>
              <div className="text-[10px] text-muted-foreground tracking-[0.2em] uppercase">Fintech Terminal</div>
            </div>
          </Link>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {navItems.map(({ to, label, icon: Icon, premium }) => {
            const active = loc.pathname === to;
            return (
              <Link key={to} to={to} data-testid={`nav-${label.toLowerCase().replace(/\s/g,'-')}`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                  active ? 'bg-saffron/10 text-saffron border border-saffron/30' : 'text-muted-foreground hover:bg-white/5 hover:text-white'
                }`}>
                <Icon size={18} weight={active ? 'fill' : 'regular'} />
                <span className="flex-1">{label}</span>
                {premium && !user?.is_premium && <span className="text-[9px] px-1.5 py-0.5 rounded bg-saffron/20 text-saffron">PRO</span>}
              </Link>
            );
          })}
          {user?.role === 'admin' && (
            <>
              <div className="pt-4 pb-2 px-3 text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Admin</div>
              <Link to="/admin" data-testid="nav-admin" className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${loc.pathname === '/admin' ? 'bg-saffron/10 text-saffron border border-saffron/30' : 'text-muted-foreground hover:bg-white/5 hover:text-white'}`}>
                <Gear size={18} /> Admin Panel
              </Link>
            </>
          )}
        </nav>
        <div className="p-3 border-t border-white/5">
          <button onClick={logout} data-testid="logout-btn" className="flex items-center gap-3 px-3 py-2.5 w-full rounded-md text-sm text-muted-foreground hover:bg-red-500/10 hover:text-red-400 transition-colors">
            <SignOut size={18} /> Logout
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 lg:ml-64 flex flex-col min-w-0">
        {/* Topbar */}
        <header className="glass border-b border-white/5 sticky top-0 z-20 h-16 flex items-center px-4 lg:px-8 gap-4">
          <div className="lg:hidden">
            <Link to="/dashboard" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg gradient-orange flex items-center justify-center">
                <Sparkle size={16} weight="fill" className="text-white" />
              </div>
              <span className="font-heading font-bold">4xview</span>
            </Link>
          </div>
          <div className="flex-1" />
          {!user?.is_premium && user?.role !== 'admin' && (
            <Button onClick={() => nav('/pricing')} data-testid="upgrade-btn" size="sm" className="bg-saffron hover:bg-saffron-hover text-white shadow-glow-saffron">
              Upgrade to Premium
            </Button>
          )}
          <button onClick={toggle} data-testid="theme-toggle" aria-label="Toggle theme" className="w-10 h-10 rounded-md hover:bg-white/5 flex items-center justify-center transition-colors">
            {theme === 'dark' ? <Sun size={18} weight="duotone" className="text-saffron" /> : <Moon size={18} weight="duotone" className="text-saffron" />}
          </button>
          <div className="relative">
            <button onClick={() => setShowNotifs(v => !v)} data-testid="notifications-btn" className="relative w-10 h-10 rounded-md hover:bg-white/5 flex items-center justify-center">
              <Bell size={18} />
              {unread > 0 && <span className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full bg-saffron text-[10px] flex items-center justify-center font-bold">{unread}</span>}
            </button>
            {showNotifs && (
              <div className="absolute right-0 top-12 w-80 glass-strong rounded-lg shadow-card-lift p-2 max-h-96 overflow-y-auto" data-testid="notifications-panel">
                <div className="flex items-center justify-between p-2 border-b border-white/5">
                  <span className="font-heading font-semibold">Notifications</span>
                  <button onClick={async () => { await api.post('/notifications/read-all'); setNotifs(notifs.map(n => ({...n, read: true}))); }} className="text-xs text-saffron">Mark all read</button>
                </div>
                {notifs.length === 0 && <div className="p-4 text-center text-muted-foreground text-sm">No notifications</div>}
                {notifs.map(n => (
                  <div key={n.id} className={`p-3 rounded-md hover:bg-white/5 cursor-pointer ${!n.read ? 'bg-saffron/5' : ''}`}>
                    <div className="text-sm font-medium">{n.title}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{n.body}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="flex items-center gap-3 pl-3 border-l border-white/10">
            <div className="text-right hidden sm:block">
              <div className="text-sm font-medium leading-none">{user?.full_name}</div>
              <div className="text-[10px] text-muted-foreground uppercase tracking-widest mt-1">
                {user?.role === 'admin' ? 'Admin' : user?.is_premium ? 'Premium' : 'Free'}
              </div>
            </div>
            <div className="w-9 h-9 rounded-full gradient-orange flex items-center justify-center font-bold text-white">
              {(user?.full_name || 'E')[0].toUpperCase()}
            </div>
          </div>
        </header>

        <main className="flex-1 p-4 lg:p-8 overflow-x-hidden">{children}</main>
      </div>
    </div>
  );
}
